import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { App, Button, Form, Typography } from "antd";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EAffiliateLanguage, EAffiliateStatus } from "@devowl-wp/api-real-commerce";
import { AffiliateSignupSidebar } from "./affiliateSignupSidebar.js";
import { AffiliateSignupSuccessState } from "./affiliateSignupSuccessState.js";
import { AffiliateTermsFields } from "./affiliateTermsFields.js";
import { RealCommerceError } from "../../../../../../exceptions/realCommerceError.js";
import { useRealCommerceErrorHandling } from "../../../../../../hooks/useRealCommerceErrorHandling.js";
import { trackAnalyticsEvent } from "../../../../../../others/tracking.js";
import { useStores } from "../../../../../../store/stores.js";
import { __ } from "../../../../../../utils/i18n.js";
import { Affiliate } from "../../../../models/affiliate.js";
import { AccountSkeleton } from "../../../accountSkeleton.js";
import { AffiliateGeneralFields } from "../invoice-payment-data/affiliateGeneralFields.js";
import { AffiliateInvoiceAddressFields } from "../invoice-payment-data/affiliateInvoiceAddressFields.js";
import { AffiliatePaymentDetailsFieldSet } from "../invoice-payment-data/affiliatePaymentDetailsFieldSet.js";
const AffiliateSignupForm = observer(()=>{
    const { message } = App.useApp();
    const [isBusy, setIsBusy] = useState(false);
    const [isLoadingInvite, setIsLoadingInvite] = useState(false);
    const [invite, setInvite] = useState();
    const [success, setSuccess] = useState();
    const [tradeRegisterExcerptFile, setTradeRegisterExcerptFile] = useState();
    const params = useMemo(()=>new URLSearchParams(window.location.search), []);
    const inviteUuid = params.get("invite") || undefined;
    const { lang } = document.documentElement;
    const communicationLanguage = lang.startsWith("de") ? EAffiliateLanguage.German : EAffiliateLanguage.English;
    const [form] = Form.useForm();
    const handleRealCommerceError = useRealCommerceErrorHandling();
    const { accountV2Store: store, accountV2Store: { session, isLoggedIn } } = useStores();
    const initialValues = {
        affiliate: {
            invoiceAddress: {},
            paymentDetails: {},
            communicationLanguage
        },
        user: {},
        interestedInProducts: [],
        terms: false,
        feeAgreement: false
    };
    if (isLoggedIn && session?.user) {
        // Prefill the known account data for a logged-in, not-yet-affiliate user.
        initialValues.affiliate.invoiceAddress.firstName = session.user.firstName;
        initialValues.affiliate.invoiceAddress.lastName = session.user.lastName;
        initialValues.user.email = session.user.email;
    }
    useEffect(()=>{
        store.fetchAffiliateProducts();
    }, []);
    // `?invite=<uuid>` deep link: resolve the invite and pre-fill before showing the form.
    useEffect(()=>{
        if (!isLoggedIn && typeof inviteUuid === "string") {
            setIsLoadingInvite(true);
            trackAnalyticsEvent("affiliate_invite_opened", {
                ga: {
                    event_category: "account",
                    event_label: "Affiliate invite opened"
                },
                matomo: {
                    category: "account",
                    name: "Affiliate invite opened"
                }
            });
            Affiliate.loadInvite(inviteUuid).then((loaded)=>{
                setInvite(loaded);
                form.setFieldsValue({
                    affiliate: {
                        communicationLanguage: loaded.communicationLanguage,
                        invoiceAddress: loaded.invoiceAddress
                    },
                    user: {
                        email: loaded.email
                    }
                });
            }).catch(()=>{
                message.warning(__("We couldn't load your invite - sign up below and we'll match it after."));
            }).finally(()=>setIsLoadingInvite(false));
        }
    }, [
        inviteUuid
    ]);
    const onFinish = useCallback(async ({ affiliate, user, interestedInProducts })=>{
        const { invoiceAddress } = affiliate;
        // Switzerland and similar: UID doubles as the tax number.
        if (!invoiceAddress.taxNumber) {
            invoiceAddress.taxNumber = invoiceAddress.companyIdentificationNumber;
        }
        setIsBusy(true);
        try {
            const { affiliate: result } = await store.createAffiliate({
                ...affiliate,
                interestedInProducts: interestedInProducts.map((id)=>({
                        id
                    })),
                invoiceAddress: {
                    ...invoiceAddress,
                    tradeRegisterExcerpt: tradeRegisterExcerptFile
                }
            }, {
                ...user,
                firstName: invoiceAddress.firstName,
                lastName: invoiceAddress.lastName
            }, invite?.uuid);
            const variant = result.status === EAffiliateStatus.Active ? "double-opt-in" : "manual-review";
            trackAnalyticsEvent("affiliate_signup_submitted", {
                ga: {
                    event_category: "account",
                    event_label: "Affiliate signup submitted",
                    value: 0
                },
                matomo: {
                    category: "account",
                    name: "Affiliate signup submitted",
                    value: variant
                }
            });
            // Logged-in path: createAffiliate already refreshed the session; the parent
            // AffiliateTab re-routes on the new affiliate / affiliateError. Anonymous path:
            // show the success panel (variant depends on whether manual review is pending).
            if (!session) {
                setSuccess({
                    variant,
                    email: user.email
                });
            }
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        } finally{
            setIsBusy(false);
        }
    }, [
        store,
        session,
        invite,
        tradeRegisterExcerptFile
    ]);
    if (isLoadingInvite) {
        return /*#__PURE__*/ _jsx(AccountSkeleton, {});
    }
    if (success) {
        return /*#__PURE__*/ _jsx(AffiliateSignupSuccessState, {
            variant: success.variant,
            email: success.email
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "rc-account-v2-affiliate-signup",
        children: [
            /*#__PURE__*/ _jsx(AffiliateSignupSidebar, {}),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-affiliate-signup__form",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-affiliate-signup__lead",
                        children: [
                            /*#__PURE__*/ _jsx(Typography.Title, {
                                level: 4,
                                style: {
                                    marginBottom: 4
                                },
                                children: __("Join the affiliate program")
                            }),
                            /*#__PURE__*/ _jsx(Typography.Paragraph, {
                                type: "secondary",
                                style: {
                                    marginBottom: 0
                                },
                                children: __("Complete your details below. We review new affiliates within three business days.")
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Form, {
                        form: form,
                        onFinish: onFinish,
                        initialValues: initialValues,
                        layout: "vertical",
                        colon: false,
                        children: [
                            /*#__PURE__*/ _jsx(AffiliateGeneralFields, {
                                isEdit: false
                            }),
                            /*#__PURE__*/ _jsx(AffiliateInvoiceAddressFields, {
                                isEdit: false,
                                setTradeRegisterExcerptFile: setTradeRegisterExcerptFile
                            }),
                            /*#__PURE__*/ _jsx(AffiliatePaymentDetailsFieldSet, {
                                form: form,
                                isEdit: false
                            }),
                            /*#__PURE__*/ _jsx(AffiliateTermsFields, {}),
                            /*#__PURE__*/ _jsx(Form.Item, {
                                style: {
                                    marginBottom: 0
                                },
                                children: /*#__PURE__*/ _jsx(Button, {
                                    type: "primary",
                                    size: "large",
                                    htmlType: "submit",
                                    block: true,
                                    loading: isBusy,
                                    children: __("Sign up as affiliate")
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
export { AffiliateSignupForm };
